import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { followCursor } from 'tippy.js';
import { ReactComponent as FilterSvg } from '../../../assets/filter.svg';
import Button from '../../../components/Button';
import FormCheckbox from '../../../components/forms/FormCheckbox';
import Block from '../../../components/generic/Block';
import BlockImg from '../../../components/generic/BlockImg';
import Flex from '../../../components/generic/Flex';
import FlexItem from '../../../components/generic/FlexItem';
import Text from '../../../components/generic/Text';
import Tippy from '../../../components/Tippy';
import { DEVICE } from '../../../constants/breakpoints';
import { fontFamilyAlternative, fontSize } from '../../../constants/font';
import { useMapFilters } from '../../../containers/MapFiltersProvider';
import { useOwnerInfo } from '../../../containers/OwnerInfoProvider';
import { getWasteBinsToShow } from '../../../utils/getWasteBinsToShow';
import useDevice from '../../../utils/hooks/useDevice';
import { px } from '../../../utils/px';
import { su } from '../../../utils/styledComponents';
import ChevronImg from './ChevronImg';
import ToggleButton from './ToggleButton';

const FilterIcon = styled(FilterSvg)`
  display: block;
`;

const Container = styled(Flex)`
  background-color: #f3f3f3;
  position: absolute;
  top: 0;
  left: 0;
  box-shadow: ${px(1)} ${px(1)} ${px(3)} 0 rgba(0, 0, 0, 0.16);
  
  transition: width 250ms ease;
  will-change: width;
  overflow: hidden;
  z-index: 3;
`;

const MobileFiltersButton = styled(Button)`
  position: absolute;
  bottom: ${su(2)};
  left: 50%;
  transform: translateX(-50%);
  
  z-index: 3;
`;

const MapFilters = ({ isOpen, setIsOpen }) => {
  const device = useDevice();
  const isMobile = device === DEVICE.mobile;
  const { mapFilters, toggleAll, toggleOne } = useMapFilters();
  const { wasteBins, mapPoints } = useOwnerInfo();
  const wasteBinsToShow = useMemo(() => getWasteBinsToShow(mapPoints, wasteBins), [mapPoints, wasteBins]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  if (isMobile && !isOpen) {
    return (
      <MobileFiltersButton width={px(205)} onClick={toggleIsOpen} padding={0}>
        <Flex alignItems="center">
          <Block marginRight={2}>
            <FilterIcon width={px(24)} />
          </Block>
          <Text color="#ffffff" fontFamily={fontFamilyAlternative}>
            Filtruj
          </Text>
        </Flex>
      </MobileFiltersButton>
    );
  }

  //explicitliy set width to 60su guarantees lack of text jumping
  return (
    <Container column width={['100%', isOpen ? 60 : 22]} maxHeight={['100%', 'auto']}>
      <FlexItem relative shrink={0}>
        <Block padding={1} width={['100%', 60]}>
          <Text>{(isOpen || isMobile) ? 'Filtruj punkty zbiórki' : 'Filtruj'}</Text>
        </Block>
        {!isMobile && (
          <ToggleButton onClick={toggleIsOpen}>
            <ChevronImg flipped={!isOpen} />
          </ToggleButton>
        )}
      </FlexItem>
      <FlexItem width={['100%', isOpen ? 60 : 'auto']} style={{ overflow: 'auto' }}>
        <Flex padding={1} alignItems="center" bg="#ffffff" marginBottom={0.2} paddingLeft={3.6}>
          <FormCheckbox
            checked={mapFilters.length === wasteBinsToShow.length}
            onChange={toggleAll}
            label={
              <Text fontSize={fontSize.small} style={{ cursor: 'pointer' }}>{(isOpen || isMobile) ? 'Wszystkie punkty zbiórki' : 'Wszystkie'}</Text>
            }
          />
        </Flex>
        <ul>
          {wasteBinsToShow.map(wasteBin => (
            <Flex
              as="li"
              alignItems="center"
              height={11}
              marginBottom={0.2}
              bg="#ffffff"
              padding={`${px(8)} ${px(10)} ${px(8)} ${px(18)}`}
              key={wasteBin.code}
            >
              <div>
                <FormCheckbox
                  checked={mapFilters.includes(wasteBin.code)}
                  onChange={() => toggleOne(wasteBin.code)}
                  label={
                    <Tippy
                      arrow={false}
                      plugins={[followCursor]}
                      followCursor
                      duration={0}
                      placement="left-end" // should be right-start, but seems reversed. https://github.com/atomiks/tippy.js-react/issues/159
                      content={
                        <Block maxWidth={px(250)}>
                          {wasteBin.mapName}
                        </Block>
                      }
                      enabled={!isOpen}
                    >
                      <Flex alignItems="center" paddingLeft={1} style={{ cursor: 'pointer' }}>
                        <FlexItem shrink={0} marginRight={2}>
                          <BlockImg src={wasteBin.icon} alt="" width={px(40)} />
                        </FlexItem>
                        {(isOpen || isMobile) && <Text fontSize={fontSize.small}>{wasteBin.mapName}</Text>}
                      </Flex>
                    </Tippy>
                  }
                />
              </div>
            </Flex>
          ))}
        </ul>
      </FlexItem>
      {isMobile && (
        <FlexItem shrink={0}>
          <Flex justifyContent="center" padding={2}>
            <Button onClick={toggleIsOpen} width={px(205)}>Zamknij filtry</Button>
          </Flex>
        </FlexItem>
      )}
    </Container>
  );
};

MapFilters.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func.isRequired,
};
export default MapFilters;
